import React, {useEffect} from 'react';

import WhiteoutWrapper from './whiteout';
import useAppI18n from './hooks/use-app-i18n';
import {useDeploymentContext} from '~/hooks/use-deployment-context';
import {DEFAULT_APP_CONTEXT} from '~/types/app-context-type';
import {hasValue, noop} from '~/utils/fns';

import type {AppContextShape} from '~/types/app-context-type';
import Selectors from "~/state/selectors.ts";
import {useSelector} from "react-redux";
import {useAppEnv} from "~/hooks/use-app-env.ts";
import axios, {InternalAxiosRequestConfig} from "axios";
import {Alert} from "reactstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {authInterceptor} from "~/interceptors/interceptors.ts";

export const AppContext = React.createContext<AppContextShape>(DEFAULT_APP_CONTEXT);

interface ComponentProps {
    children: JSX.Element;
}

export const AppInit = ({children}: ComponentProps): JSX.Element => {
    useDeploymentContext();
    useAppEnv();

    const cnum = useSelector(Selectors.getCustomerNumber);
    const ctx = useSelector(Selectors.getAppContext);
    const appEnvState = useSelector(Selectors.getAppEnv);

    const {
        fetchLocale,
        fetchingLocale,
    } = useAppI18n(ctx);

    const appEnv = appEnvState.env;
    useEffect(() => {
        if (!hasValue(appEnv?.oidc) || appEnv?.oidc?.clientId === '') {
            return;
        }

        axios.interceptors.request.use((config: InternalAxiosRequestConfig): InternalAxiosRequestConfig =>
            authInterceptor(config, appEnv?.oidc?.clientId));
    }, [appEnv]);

    const deploymentContextError = useSelector(Selectors.getDeploymentContextError);
    if (cnum === '' && deploymentContextError === 'Invalid cnum') {
        return (
            <WhiteoutWrapper>
                <Alert color="danger">
                    <FontAwesomeIcon className="me-2" icon="exclamation-circle"/>
                    <b>Invalid customer number!</b>
                    <br/>
                    Please include your customer number in the URL.
                    <br/>
                    Example: https://.../.../#/C12345
                </Alert>
            </WhiteoutWrapper>
        );
    }

    if (appEnv === null) {
        return (
            <WhiteoutWrapper>
                {appEnvState.fetchingEnv ? 'Loading' : <div/>}
            </WhiteoutWrapper>
        );
    }

    return (
        <AppContext.Provider value={{
            addLanguage: noop,
            fetchingLocale: fetchingLocale,
            fetchLocale: fetchLocale
        }}>
            {children}
        </AppContext.Provider>
    );
};

export default AppInit;
